import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import type { CommunityTemplateGalleryRead, TemplatePackRead } from '../../types/api';
import { useBrandIdentity } from '../knowledge-base/useBrandIdentity';
import { useCollection } from '../knowledge-base/useCollection';
import { useBusiness } from '../onboarding/useBusiness';
import { CommunityGallery } from './CommunityGallery';
import { COMMUNITY_TEMPLATE_STATE_KEY } from './communityTemplatePrefill';
import { communityTemplatesApi } from './communityTemplatesApi';
import { brandPalette } from './communityZones';
import { PackGallery } from './PackGallery';
import { useCommunityGallery } from './useCommunityGallery';
import { useStartFromOwnTemplate } from './useStartFromOwnTemplate';
import { useTemplatePacks } from './useTemplatePacks';
import { YourCommunityTemplates } from './YourCommunityTemplates';

/** The two kinds of template, and the tab each one lives on. */
const TEMPLATE_TABS = [
  { key: 'packs', label: 'Layouts' },
  { key: 'community', label: 'Community' },
] as const;

type TemplateTab = (typeof TEMPLATE_TABS)[number]['key'];

const TAB_PARAM = 'tab';
const DEFAULT_TAB: TemplateTab = 'packs';

function isTemplateTab(value: string | null): value is TemplateTab {
  return TEMPLATE_TABS.some((tab) => tab.key === value);
}

/** The pack tab: fixed niche layouts the deployment ships. */
function PacksTab({ onUse }: { onUse: (pack: TemplatePackRead) => void }) {
  const { packs, isLoading, error } = useTemplatePacks();

  return (
    <>
      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      {isLoading && packs.length === 0 ? (
        <p className="kb-section__status" role="status">
          Loading the templates
        </p>
      ) : (
        <PackGallery packs={packs} onUse={onUse} />
      )}
    </>
  );
}

/**
 * The community tab: the business's own templates in every status, then the
 * designs members made, approved and shared.
 *
 * An own template that is approved is listed once, under "Yours", and taken
 * out of the shared grid below: the same design twice on one screen reads as
 * two templates, and its Approved chip already says it is shared.
 */
function CommunityTab({ onUse }: { onUse: (template: CommunityTemplateGalleryRead) => void }) {
  const { templates, isLoading, error } = useCommunityGallery();
  // Brand-role colours preview in the viewer's own palette — what they would get.
  const { businessId } = useBusiness();
  const { identity } = useBrandIdentity(businessId);
  const own = useCollection(communityTemplatesApi, businessId);
  const { start, startingId } = useStartFromOwnTemplate();
  const palette = brandPalette(identity?.colors);

  const ownIds = new Set(own.items.map((item) => item.id));
  const shared = templates.filter((template) => !ownIds.has(template.id));

  return (
    <CommunityGallery
      templates={shared}
      isLoading={isLoading}
      error={error}
      onUse={onUse}
      palette={palette}
      hasOwn={own.items.length > 0}
      yours={
        businessId ? (
          <YourCommunityTemplates
            templates={own.items}
            isLoading={own.isLoading}
            error={own.error}
            onUse={(template) => void start(template)}
            startingId={startingId}
            palette={palette}
          />
        ) : null
      }
    />
  );
}

/**
 * The template gallery at `/templates` — the third way to start a post, beside
 * a written brief and the newsfeed hand-off.
 *
 * Two tabs over one idea. **Layouts** are the packs the deployment ships: a
 * fixed niche layout (a team welcome, a menu, an offer) whose content the user
 * types straight into the brief. **Community** is the designs members made
 * themselves and shared with every other business — the artwork is theirs, and
 * only the caption, logo and photo are placed on it. Neither spends anything on
 * the design or image models.
 *
 * Picking a card hands its template to `/generation` in router state, exactly
 * the way a news card hands over an article. The open tab lives in the URL so a
 * link can name one.
 */
export function TemplatesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const requested = searchParams.get(TAB_PARAM);
  const tab = isTemplateTab(requested) ? requested : DEFAULT_TAB;

  function openTab(next: TemplateTab) {
    // Replace, so browser back leaves the gallery instead of walking tabs.
    setSearchParams({ [TAB_PARAM]: next }, { replace: true });
  }

  function startFromPack(pack: TemplatePackRead) {
    void navigate('/generation', { state: { templatePack: pack } });
  }

  function startFromCommunity(template: CommunityTemplateGalleryRead) {
    void navigate('/generation', { state: { [COMMUNITY_TEMPLATE_STATE_KEY]: template } });
  }

  return (
    <main className="page page--wide">
      <PageHeader
        section="Templates"
        title="Templates"
        lede="Ready-made layouts for the posts every shop makes, your own designs, and designs other members have shared. Fill in the fields and the post is built straight from them — it uses one post."
      />

      <div className="tabs" role="tablist" aria-label="Template kinds">
        {TEMPLATE_TABS.map((entry) => (
          <button
            className={entry.key === tab ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
            key={entry.key}
            type="button"
            role="tab"
            id={`templates-tab-${entry.key}`}
            aria-selected={entry.key === tab}
            aria-controls={`templates-panel-${entry.key}`}
            onClick={() => openTab(entry.key)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div
        className="tabs__panel"
        role="tabpanel"
        id={`templates-panel-${tab}`}
        aria-labelledby={`templates-tab-${tab}`}
      >
        {tab === 'packs' ? (
          <PacksTab onUse={startFromPack} />
        ) : (
          <CommunityTab onUse={startFromCommunity} />
        )}
      </div>

      <p className="pack-gallery__foot">
        Rather write it yourself? <Link to="/generation">Start from scratch</Link> and we’ll lay the
        post out for you.
      </p>
    </main>
  );
}
